import React from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ConfigSchema,
  ConfigValues,
  defaultsFromFields,
  fieldVisible,
  groupValues,
  hexToUint8Array,
  isObjectItemSpec,
  SchemaField,
  setGroupField,
  uint8ArrayToHex,
} from '../schema/schema';
import {
  schemaFieldDescription,
  schemaFieldTitle,
  schemaGroupTitle,
  schemaOptionLabel,
  useI18n,
} from '../i18n';

interface SchemaFormProps {
  schema: ConfigSchema;
  values: ConfigValues;
  onChange: (values: ConfigValues) => void;
  disabled?: boolean;
  groupIds?: string[];
}

interface SchemaObjectFormProps {
  groupId: string;
  fields: Record<string, SchemaField>;
  values: ConfigValues;
  onChange: (values: ConfigValues) => void;
  disabled?: boolean;
}

export const SchemaObjectForm: React.FC<SchemaObjectFormProps> = ({
  groupId,
  fields,
  values,
  onChange,
  disabled,
}) => {
  const { t } = useI18n();
  const visibleKeys = Object.keys(fields || {}).filter((key) => {
    const field = fields[key];
    return field && fieldVisible(field, values);
  });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {visibleKeys.map((key) => (
        <Box key={key}>
          {renderField(
            t,
            groupId,
            key,
            fields[key],
            values[key],
            disabled || Boolean(fields[key].readOnly),
            (value) => onChange({ ...values, [key]: value }),
          )}
        </Box>
      ))}
    </Box>
  );
};

const SchemaForm: React.FC<SchemaFormProps> = ({ schema, values, onChange, disabled, groupIds }) => {
  const groups = groupIds
    ? schema.groups.filter((group) => groupIds.includes(group.id))
    : schema.groups;
  return (
    <Box>
      {groups.map((group) => {
        const current = groupValues(values, group);
        const visibleKeys = Object.keys(group.fields || {}).filter((key) => {
          const field = group.fields[key];
          return field && fieldVisible(field, current);
        });
        if (visibleKeys.length === 0) {
          return null;
        }
        return (
          <Box key={group.id} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {schemaGroupTitle(group.id)}
            </Typography>
            <SchemaObjectForm
              groupId={group.id}
              fields={group.fields}
              values={current}
              disabled={disabled}
              onChange={(next) => {
                let merged = values;
                for (const key of Object.keys(group.fields || {})) {
                  merged = setGroupField(merged, group, key, next[key]);
                }
                onChange(merged);
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

function renderField(
  t: (key: string, vars?: Record<string, string | number>) => string,
  groupId: string,
  key: string,
  field: SchemaField,
  value: unknown,
  disabled: boolean,
  onChange: (value: unknown) => void,
) {
  const title = schemaFieldTitle(groupId, key, field.title);
  const helper = schemaFieldDescription(groupId, key) || field.description;
  if (field.type === 'array' && isObjectItemSpec(field.item)) {
    const rows = Array.isArray(value) ? [...(value as ConfigValues[])] : [];
    const itemFields = field.item.fields;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        {helper && <Typography variant="caption" color="text.secondary" display="block">{helper}</Typography>}
        {rows.map((row, index) => (
          <Box key={index} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, mt: 0.5 }}>
            <SchemaObjectForm
              groupId={`${groupId}_${key}`}
              fields={itemFields}
              values={row && typeof row === 'object' ? row : {}}
              disabled={disabled}
              onChange={(next) => {
                const copy = [...rows];
                copy[index] = next;
                onChange(copy);
              }}
            />
            <Button size="small" color="error" disabled={disabled} onClick={() => onChange(rows.filter((_, i) => i !== index))}>
              {t('schemaForm.removeItem')}
            </Button>
          </Box>
        ))}
        <Button
          size="small"
          disabled={disabled}
          onClick={() => onChange([...rows, defaultsFromFields(itemFields)])}
          sx={{ mt: 0.5 }}
        >
          {t('schemaForm.addItem')}
        </Button>
      </Box>
    );
  }
  if (field.type === 'array' && field.item && !isObjectItemSpec(field.item) && field.item.type === 'string') {
    const fallback = Array.isArray(field.default) ? (field.default as unknown[]).map((item) => String(item ?? '')) : [];
    const raw = Array.isArray(value) ? (value as unknown[]).map((item) => String(item ?? '')) : [];
    const len = Math.max(fallback.length, raw.length, 1);
    const rows = Array.from({ length: len }, (_, index) => raw[index] ?? fallback[index] ?? '');
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        {helper && <Typography variant="caption" color="text.secondary" display="block">{helper}</Typography>}
        {rows.map((row, index) => (
          <TextField
            key={index}
            size="small"
            fullWidth
            label={t('deviceConfig.stripRef', { index: index + 1 })}
            value={row}
            disabled={disabled}
            sx={{ mt: 0.5 }}
            onChange={(e) => {
              const copy = [...rows];
              copy[index] = e.target.value;
              onChange(copy);
            }}
          />
        ))}
      </Box>
    );
  }
  if (field.type === 'array' && field.item && !isObjectItemSpec(field.item) && (field.item.type === 'integer' || field.item.type === 'number')) {
    const item = field.item;
    const fallback = Array.isArray(field.default)
      ? (field.default as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : [];
    const nums = Array.isArray(value)
      ? (value as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : fallback;
    const rows = nums.length ? nums : fallback;
    const maxItems = Number(field.maxItems) > 0 ? Number(field.maxItems) : 4;
    const itemTitle = item.title || title;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        {helper && <Typography variant="caption" color="text.secondary" display="block">{helper}</Typography>}
        {rows.map((row, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label={`${itemTitle} ${index + 1}`}
              value={row}
              disabled={disabled}
              inputProps={{ min: item.minimum, max: item.maximum, step: 1 }}
              onChange={(e) => {
                const next = parseInt(e.target.value, 10);
                const copy = [...rows];
                copy[index] = Number.isNaN(next) ? 0 : next;
                onChange(copy);
              }}
            />
            <Button
              size="small"
              color="error"
              disabled={disabled || rows.length <= 1}
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              {t('schemaForm.removeItem')}
            </Button>
          </Box>
        ))}
        <Button
          size="small"
          disabled={disabled || rows.length >= maxItems}
          onClick={() => onChange([...rows, Number(item.default) || 0])}
          sx={{ mt: 0.5 }}
        >
          {t('schemaForm.addItem')}
        </Button>
      </Box>
    );
  }
  if (field.type === 'object' && field.fields) {
    const nested = value && typeof value === 'object' && !Array.isArray(value) ? value as ConfigValues : {};
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        {helper && <Typography variant="caption" color="text.secondary" display="block">{helper}</Typography>}
        <SchemaObjectForm
          groupId={`${groupId}_${key}`}
          fields={field.fields}
          values={nested}
          disabled={disabled}
          onChange={(next) => onChange(next)}
        />
      </Box>
    );
  }
  if (field.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label={title}
      />
    );
  }
  if (field.options && field.options.length > 0) {
    return (
      <TextField
        select
        size="small"
        fullWidth
        label={title}
        helperText={helper}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(coerceOption(e.target.value, field))}
      >
        {field.options.map((option) => (
          <MenuItem key={String(option.value)} value={String(option.value)}>
            {schemaOptionLabel(groupId, key, option.value, option.label)}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  if (field.type === 'uint8-array') {
    const length = field.length || 0;
    return (
      <TextField
        size="small"
        fullWidth
        label={title}
        helperText={helper || t('schemaForm.hexBytes', { count: length })}
        value={uint8ArrayToHex(value, length)}
        disabled={disabled}
        type={field.secret ? 'password' : 'text'}
        onChange={(e) => onChange(hexToUint8Array(e.target.value, length))}
      />
    );
  }
  if (field.type === 'json' || (field.type === 'array' && field.itemTypes)) {
    const text =
      typeof value === 'string'
        ? value
        : JSON.stringify(value ?? (field.type === 'json' && !Array.isArray(field.default) ? {} : []), null, 2);
    return (
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={6}
        label={title}
        helperText={helper}
        value={text}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          try {
            onChange(JSON.parse(raw));
          } catch {
            onChange(raw);
          }
        }}
        onBlur={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            /* leave as-is */
          }
        }}
      />
    );
  }
  if (field.type === 'integer' || field.type === 'number') {
    return (
      <TextField
        size="small"
        fullWidth
        type="number"
        label={title}
        helperText={helper}
        value={value ?? ''}
        disabled={disabled}
        inputProps={{ min: field.minimum, max: field.maximum, step: field.type === 'integer' ? 1 : undefined }}
        onChange={(e) => {
          const next = field.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value);
          onChange(Number.isNaN(next) ? 0 : next);
        }}
      />
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      label={title}
      helperText={helper}
      value={value ?? ''}
      disabled={disabled}
      type={field.type === 'password' || field.secret ? 'password' : 'text'}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function coerceOption(raw: string, field: SchemaField): string | number | boolean {
  const match = field.options?.find((option) => String(option.value) === raw);
  if (!match) {
    return raw;
  }
  return match.value;
}

export default SchemaForm;
